import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Box, Button, Card, CircularProgress, Grow, Icon, Popper, Stack, Tooltip } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";

import { LAYOUT_MARGIN } from "Configs/home";

import { useAppSelector } from "Hooks/useRedux";
import useStations from "Hooks/useStations";
import useTrafficPath from "Hooks/useTrafficPath";

import { toggleVisibility as toggleLocationData } from "Redux/locationDataSlice";
import { setMapData } from "Redux/mapSlice";
import useTravels from "Hooks/useTravels";
import useCameras from "Hooks/useCamera";
import useTraffic from "Hooks/useTraffic";
import useTravelCount from "Hooks/useTravelCount";
import useTrafficCount from "Hooks/useTrafficCount";
import useTrafficFleet from "Hooks/useTrafficFleet";
import useEvenets from "Hooks/useEvenets";
import usePassages from "Hooks/usePassages";
import useWaybill from "Hooks/useWaybill";
import useMarginPark from "Hooks/useMarginPark";

import TravelImg from "Assets/Images/trfficAnalysis/travel.svg";
import TrafficImg from "Assets/Images/trfficAnalysis/traffic.svg";
import PassageImg from "Assets/Images/trfficAnalysis/passage.svg";
import StationImg from "Assets/Images/trfficAnalysis/station.svg";
import FleetImg from "Assets/Images/trfficAnalysis/fleet.svg";
import MarginParkImg from "Assets/Images/trfficAnalysis/marginPark.svg";
import PathImg from "Assets/Images/trfficAnalysis/path.svg";
import EventImg from "Assets/Images/trfficAnalysis/events.svg";
import CameraImg from "Assets/Images/trfficAnalysis/camera.svg";
import WaybilImg from "Assets/Images/trfficAnalysis/waybill.svg";

import TravelImgWhite from "Assets/Images/trfficAnalysis/white/travel-white.svg";
import TrafficImgWhite from "Assets/Images/trfficAnalysis/white/traffic-white.svg";
import PassageImgWhite from "Assets/Images/trfficAnalysis/white/passage-white.svg";
import StationImgWhite from "Assets/Images/trfficAnalysis/white/station-white.svg";
import FleetImgWhite from "Assets/Images/trfficAnalysis/white/fleet-white.svg";
import MarginParkImgWhite from "Assets/Images/trfficAnalysis/white/marginPark-white.svg";
import PathImgWhite from "Assets/Images/trfficAnalysis/white/path-white.svg";
import EventImgWhite from "Assets/Images/trfficAnalysis/white/events-white.svg";
import CameraImgWhite from "Assets/Images/trfficAnalysis/white/camera-white.svg";
import WaybilImgWhite from "Assets/Images/trfficAnalysis/white/waybill-white.svg";

import { FleetIcon, MarginParkIcon } from "Components/customIcons/fleet";

import Delete from "./Delete";
import Filter from "./Filter";
import Time from "./Time";
import Stations from "./Stations";
import Paths from "./Paths";
import Zone from "./Zone";
import Travel from "./Travel";
import Traffic from "./Traffic";
import LayerControl from "./layerControl";
import Fleet from "./Fleet";
import Camera from "./Camera";
import Events from "./Events";
import Waybill from "./Waybill";
import Passage from "./Passage";
import MarginPark from "./MarginPark";

type OptionType = "Menu" | "Button" | "Toggle";
interface IOption {
  icon: string;
  img?: any;
  whiteImg?: any;
  active?: boolean;
  type: OptionType;
  toolTipText?: string;
  content?: any;
  onClick?: () => void;
}

export default function Toolbox() {
  const dispatch = useDispatch();

  const locationData = useAppSelector((state) => state.locationData);
  const travel = useAppSelector((state) => state.travel);
  const traffic = useAppSelector((state) => state.traffic);
  const trafficFleet = useAppSelector((state) => state.trafficFleet);
  const zones = useAppSelector((state) => state.trafficZones);
  const camera = useAppSelector((state) => state.camera);
  const event = useAppSelector((state) => state.event);
  const waybill = useAppSelector((state) => state.waybill);
  const passage = useAppSelector((state) => state.passage);
  const marginPark = useAppSelector((state) => state.marginPark);
  const stations = useAppSelector((state) => state.trafficStations);
  const trafficPath = useAppSelector((state) => state.trafficPath);
  const { zoom } = useAppSelector((state) => state.map);
  const { mode } = useAppSelector((state) => state.fleet);
  const { page } = useAppSelector((state) => state.page);

  const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openTooltip, setOpenTooltip] = useState<number | null>(null);

  const { isLoading: isLoadingTravels } = useTravels();
  const { isLoading: isLoadingTravelCount } = useTravelCount();
  const { isLoading: isLoadingTrafficCount } = useTrafficCount();
  const { isLoading: isLoadingTraffics } = useTraffic();
  const { isLoading: isLoadingTrafficFleet } = useTrafficFleet();
  const { isLoading: isLoadingCameras } = useCameras();
  const { isLoading: isLoadingEvents } = useEvenets();
  const { isLoading: isLoadingWaybill } = useWaybill();
  const { isLoading: isLoadingPassage, isFetching: isFetchingPassage } = usePassages();
  const { isLoading: isLoadingMarginPark } = useMarginPark();
  const { isLoading: isLoadingPaths } = useTrafficPath();
  const { isLoading: isLoadingStations } = useStations();

  const Options: IOption[] = [
    {
      icon: "date_range",
      type: "Menu",
      content: <Time />,
      toolTipText: " تاریخ",
    },
    {
      icon: "filter_alt",
      type: "Menu",
      content: <Filter />,
      toolTipText: "فیلتر براساس مبدا و مقصد",
    },
    {
      icon: "assistant",
      img: TravelImg,
      whiteImg: TravelImgWhite,
      type: "Menu",
      active: travel.visible,
      content: <Travel />,
      toolTipText: "سفر",
    },
    {
      icon: "add_a_photo",
      img: TrafficImg,
      whiteImg: TrafficImgWhite,
      type: "Menu",
      active: traffic.visible,
      content: <Traffic />,
      toolTipText: "تردد",
    },
    {
      icon: "directions_car",
      img: FleetImg,
      whiteImg: FleetImgWhite,
      type: "Menu",
      active: Boolean(mode),
      content: <Fleet />,
      toolTipText: "ناوگان",
    },
    {
      icon: "videocam",
      img: CameraImg,
      whiteImg: CameraImgWhite,
      type: "Menu",
      active: camera.visible,
      content: <Camera />,
      toolTipText: "دوربین",
    },
    {
      icon: "warning",
      img: EventImg,
      whiteImg: EventImgWhite,
      type: "Menu",
      active: event.visible,
      content: <Events />,
      toolTipText: "حوادث",
    },
    {
      icon: "drive_file_move",
      img: WaybilImg,
      whiteImg: WaybilImgWhite,
      type: "Menu",
      active: waybill.visible,
      content: <Waybill />,
      toolTipText: "بارنامه",
    },
    {
      icon: "map",
      img: PassageImg,
      whiteImg: PassageImgWhite,
      type: "Menu",
      active: passage.visible,
      content: <Passage />,
      toolTipText: "ترافیک",
    },
    {
      icon: "local_parking",
      img: MarginParkImg,
      whiteImg: MarginParkImgWhite,
      type: "Menu",
      active: marginPark.visible,
      content: <MarginPark />,
      toolTipText: "پارک حاشیه ای",
    },
    {
      icon: "directions_bus",
      img: StationImg,
      whiteImg: StationImgWhite,
      type: "Menu",
      active: stations.visible,
      content: <Stations />,
      toolTipText: "ایستگاه ها",
    },
    {
      icon: "route",
      img: PathImg,
      whiteImg: PathImgWhite,
      type: "Menu",
      active: trafficPath.visible,
      toolTipText: "مسیرهای تعریف شده",
      content: <Paths />,
    },
    {
      icon: "layers",
      type: "Menu",
      active: zones.visible,
      toolTipText: "انتخاب لایه زون ها",
      content: <Zone />,
    },
    {
      icon: "delete",
      type: "Menu",
      toolTipText: "حذف فیلتر ها",
      content: <Delete setActiveOptionIndex={setActiveOptionIndex} />,
    },
    {
      icon: "dynamic_feedIcon",
      type: "Menu",
      content: <LayerControl />,
      toolTipText: "جابجایی لایه ها",
    },
    {
      icon: "room",
      type: "Toggle",
      toolTipText: "نمایش آدرس",
      active: locationData.visible,
      onClick: () => dispatch(toggleLocationData()),
    },
  ];

  const handleOpenTooltip = (index: number | null) => {
    setOpenTooltip(index);
  };

  const handleBtnColor = (isActive: boolean, isActivate?: boolean) => {
    // if (index === 5 && zones.visible) {
    //   return "contained";
    // }
    if (isActivate || isActive) {
      return "contained";
    }
    return "white";
  };

  // Logic functions
  const handleClick = (e: React.MouseEvent<HTMLElement>, index: number, item: IOption) => {
    const { currentTarget } = e;

    switch (item.type) {
      case "Menu":
        if (activeOptionIndex === index) {
          setActiveOptionIndex(null);
          return;
        }
        if (activeOptionIndex === null) {
          setAnchorEl(currentTarget);
          setActiveOptionIndex(index);
        } else {
          setActiveOptionIndex(null);
        }

        setTimeout(() => {
          setAnchorEl(currentTarget);
          setActiveOptionIndex(index);
        }, 250);
        break;
      case "Toggle":
        if (item.onClick) {
          item.onClick();
        }
        break;

      default:
        break;
    }
  };

  const handleMapZoom = (index: number) => {
    const zoomLevel = 0.5;
    const newZoom = index === 0 ? zoom + zoomLevel : zoom - zoomLevel;

    dispatch(setMapData({ zoom: newZoom }));
  };

  const handleShowIcon = (
    index: number,
    isActive: boolean,
    iconName: string,
    imgSrc?: string,
    whiteImgSrc?: string,
    isActivate?: any
  ) => {
    if (index === 2 && travel.visible) {
      if ((travel.travelShowType === 0 || travel.travelShowType === 1) && isLoadingTravels) {
        return <CircularProgress color="secondary" size={30} />;
      }
      if (travel.travelShowType === 2 && isLoadingTravelCount) {
        return <CircularProgress color="secondary" size={30} />;
      }
    }
    if (index === 3 && traffic.visible) {
      if ((traffic.trafficShowType === 0 || traffic.trafficShowType === 1) && isLoadingTraffics) {
        return <CircularProgress color="secondary" size={30} />;
      }
      if (traffic.trafficShowType === 2 && isLoadingTrafficCount) {
        return <CircularProgress color="secondary" size={30} />;
      }
    }
    if (index === 4 && isLoadingTrafficFleet && trafficFleet.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 5 && isLoadingCameras && camera.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 6 && isLoadingEvents && event.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 7 && isLoadingWaybill && waybill.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 8 && isFetchingPassage && passage.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 9 && isLoadingMarginPark && marginPark.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 10 && isLoadingStations && stations.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (index === 11 && isLoadingPaths && trafficPath.visible) {
      return <CircularProgress color="secondary" size={30} />;
    }
    if (imgSrc) {
      if (isActivate || isActive) {
        return <img src={whiteImgSrc} alt={iconName} style={{ width: "28px" }} />;
      }
      return <img src={imgSrc} alt={iconName} style={{ width: "28px" }} />;
    }
    return <Icon baseClassName={!isActive ? "material-icons-outlined" : "material-icons"}>{iconName}</Icon>;
  };

  // UI functions
  const renderOptions = () =>
    Options.map((item, index) => {
      const isActive = index === activeOptionIndex;

      return (
        <div key={item.icon}>
          <Tooltip
            title={item.toolTipText}
            open={isActive ? false : openTooltip === index}
            onOpen={() => handleOpenTooltip(index)}
            onClose={() => handleOpenTooltip(null)}
            placement="top"
          >
            <Button
              sx={{ minWidth: "56px", p: 0, position: "relative", zIndex: "1" }}
              variant={handleBtnColor(isActive, item?.active)}
              size="large"
              aria-describedby={`option-${index}`}
              onClick={(e) => handleClick(e, index, item)}
            >
              {handleShowIcon(index, isActive, item.icon, item.img, item.whiteImg, item?.active)}
              <Box
                sx={{
                  position: "absolute",
                  bottom: "8px",
                  opacity: item.type === "Menu" && index === activeOptionIndex ? "1" : "0",
                  transition: "all 0.3s",
                }}
              >
                <Stack direction="row" spacing={0.3}>
                  {[1, 2, 3].map((i) => (
                    <Box key={`dot-${i}`} sx={{ width: "4px", height: "4px", bgcolor: "#fff", borderRadius: "50%" }} />
                  ))}
                </Stack>
              </Box>
            </Button>
          </Tooltip>

          <Popper
            id={`item-${index}`}
            sx={{ zIndex: "1" }}
            open={index === activeOptionIndex}
            anchorEl={anchorEl}
            placement="top"
            transition
            modifiers={[
              {
                name: "preventOverflow",
                enabled: true,
                options: { rootBoundary: "viewport", padding: LAYOUT_MARGIN + 56 + 8 },
              },
            ]}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Box sx={{ mb: 1 }}>{item.content} </Box>
              </Grow>
            )}
          </Popper>
        </div>
      );
    });

  return (
    <Stack direction="row" spacing={1}>
      {renderOptions()}
    </Stack>
  );
}
